 
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
  ActivityIndicator,
  Switch,
  Image,
  Platform,
  KeyboardAvoidingView,
  Dimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// Enhanced Context imports with fallbacks
let useProfessional;
try {
  useProfessional = require('../../context/ProfessionalContext').useProfessional;
} catch (error) {
  console.warn('ProfessionalContext not found, using fallback');
  useProfessional = () => ({
    userData: {
      full_name: 'Professional User',
      email: 'user@example.com',
      phone: '+44 7700 900123',
      location: 'London, UK',
      profession: 'Engineering Professional',
      profile_image: null,
    },
    updateUserData: () => Promise.resolve(),
    preferences: {
      notifications: true,
      email_updates: true,
      sms_notifications: false,
      marketing_emails: false,
      two_factor_auth: false,
      dark_mode: false,
      language: 'English',
    },
    updatePreferences: () => Promise.resolve(),
  });
}
// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

export default function AccountSettingsScreen() {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const navigation = useNavigation();
  
  // Context data with fallback
  const {
    userData,
    updateUserData,
    preferences,
    updatePreferences,
  } = useProfessional();

  // Form states
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    full_name: userData?.full_name || '',
    email: userData?.email || '',
    phone: userData?.phone || '',
    location: userData?.location || '',
    profession: userData?.profession || '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  // Settings states
  const [settings, setSettings] = useState({
    notifications: preferences?.notifications || true,
    email_updates: preferences?.email_updates || true,
    sms_notifications: preferences?.sms_notifications || false,
    marketing_emails: preferences?.marketing_emails || false,
    two_factor_auth: preferences?.two_factor_auth || false,
    dark_mode: preferences?.dark_mode || false,
    language: preferences?.language || 'English',
  });

  // Validation states
  const [errors, setErrors] = useState({});
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    loadAccountData();
  }, []);

  const loadAccountData = async () => {
    try {
      setIsLoading(true);
      
      // Load from AsyncStorage as fallback
      const storedData = await AsyncStorage.getItem('accountSettings');
      if (storedData) {
        const parsed = JSON.parse(storedData);
        setFormData(prev => ({ ...prev, ...parsed.formData }));
        setSettings(prev => ({ ...prev, ...parsed.settings }));
      }
    } catch (error) {
      console.error('Error loading account data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    } else if (formData.full_name.trim().length < 2) {
      newErrors.full_name = 'Name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (formData.phone && !phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Password validation (if changing password)
    if (showPasswordFields) {
      if (!formData.current_password) {
        newErrors.current_password = 'Current password is required';
      }

      if (!formData.new_password) {
        newErrors.new_password = 'New password is required';
      } else if (formData.new_password.length < 8) {
        newErrors.new_password = 'Password must be at least 8 characters';
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.new_password)) {
        newErrors.new_password = 'Password must contain uppercase, lowercase, and number';
      }

      if (formData.new_password !== formData.confirm_password) {
        newErrors.confirm_password = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveChanges = async () => {
    if (!validateForm()) {
      Alert.alert('Validation Error', 'Please fix the errors before saving.');
      return;
    }

    try {
      setIsSaving(true);

      // Save to context if available
      if (updateUserData) {
        await updateUserData({
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          location: formData.location,
          profession: formData.profession,
        });
      }

      if (updatePreferences) {
        await updatePreferences(settings);
      }

      // Save to AsyncStorage
      const dataToSave = {
        formData: {
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          location: formData.location,
          profession: formData.profession,
        },
        settings,
        lastUpdated: new Date().toISOString(),
      };
      
      await AsyncStorage.setItem('accountSettings', JSON.stringify(dataToSave));

      // Clear password fields
      setFormData(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));
      setShowPasswordFields(false);

      Alert.alert(
        'Success',
        'Your account settings have been updated successfully.',
        [{ text: 'OK', onPress: () => navigation.goBack() }]
      );
    } catch (error) {
      console.error('Error saving changes:', error);
      Alert.alert('Error', 'Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = () => {
    Alert.alert(
      'Delete Account',
      'Are you sure you want to delete your account? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            Alert.alert(
              'Account Deleted',
              'Your account has been scheduled for deletion. You will receive a confirmation email.',
              [{ text: 'OK', onPress: () => navigation.navigate('LoginScreen') }]
            );
          },
        },
      ]
    );
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const updateSetting = (setting, value) => {
    setSettings(prev => ({ ...prev, [setting]: value }));
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <Ionicons name="arrow-back" size={24} color="#ffffff" />
      </TouchableOpacity>
      <Text style={styles.headerTitle}>Account Settings</Text>
      <View style={styles.headerRight} />
    </View>
  );

  const renderFormField = (field, placeholder, icon, options = {}) => (
    <View style={styles.fieldContainer}>
      <Text style={styles.fieldLabel}>{placeholder}</Text>
      <View style={[styles.inputContainer, errors[field] && styles.inputError]}>
        <MaterialCommunityIcons name={icon} size={20} color="#64748b" style={styles.inputIcon} />
        <TextInput
          style={styles.input}
          placeholder={placeholder}
          value={formData[field]}
          onChangeText={(value) => updateFormData(field, value)}
          autoCapitalize={options.autoCapitalize || 'words'}
          keyboardType={options.keyboardType || 'default'}
          secureTextEntry={options.secureTextEntry}
          {...options}
        />
        {options.secureTextEntry && (
          <TouchableOpacity
            style={styles.eyeIcon}
            onPress={() => {
              if (field === 'current_password') setShowCurrentPassword(!showCurrentPassword);
              if (field === 'new_password') setShowNewPassword(!showNewPassword);
              if (field === 'confirm_password') setShowConfirmPassword(!showConfirmPassword);
            }}
          >
            <Ionicons 
              name={
                (field === 'current_password' && showCurrentPassword) ||
                (field === 'new_password' && showNewPassword) ||
                (field === 'confirm_password' && showConfirmPassword)
                  ? 'eye-off' : 'eye'
              } 
              size={20} 
              color="#64748b" 
            />
          </TouchableOpacity>
        )}
      </View>
      {errors[field] && (
        <Text style={styles.errorText}>{errors[field]}</Text>
      )}
    </View>
  );

  const renderSettingItem = (title, subtitle, setting, type = 'switch') => (
    <View style={styles.settingItem}>
      <View style={styles.settingContent}>
        <Text style={styles.settingTitle}>{title}</Text>
        {subtitle && <Text style={styles.settingSubtitle}>{subtitle}</Text>}
      </View>
      {type === 'switch' && (
        <Switch
          value={settings[setting]}
          onValueChange={(value) => updateSetting(setting, value)}
          trackColor={{ false: '#e2e8f0', true: '#0e4e96' }}
          thumbColor={settings[setting] ? '#ffffff' : '#f4f3f4'}
        />
      )}
      {type === 'arrow' && (
        <Ionicons name="chevron-forward" size={20} color="#64748b" />
      )}
    </View>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#0e4e96" />
        <Text style={styles.loadingText}>Loading account settings...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      
      {renderHeader()}
      
      <KeyboardAvoidingView 
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView 
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Profile Information Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Profile Information</Text>
            
            {renderFormField('full_name', 'Full Name', 'account')}
            {renderFormField('email', 'Email Address', 'email', { 
              keyboardType: 'email-address',
              autoCapitalize: 'none'
            })}
            {renderFormField('phone', 'Phone Number', 'phone', { 
              keyboardType: 'phone-pad'
            })}
            {renderFormField('location', 'Location', 'map-marker')}
            {renderFormField('profession', 'Profession', 'briefcase')}
          </View>

          {/* Security Section */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Security</Text>
              <TouchableOpacity
                style={styles.changePasswordButton}
                onPress={() => setShowPasswordFields(!showPasswordFields)}
              >
                <Text style={styles.changePasswordText}>
                  {showPasswordFields ? 'Cancel' : 'Change Password'}
                </Text>
              </TouchableOpacity>
            </View>

            {showPasswordFields && (
              <>
                {renderFormField('current_password', 'Current Password', 'lock', {
                  secureTextEntry: !showCurrentPassword,
                  autoCapitalize: 'none'
                })}
                {renderFormField('new_password', 'New Password', 'lock-plus', {
                  secureTextEntry: !showNewPassword,
                  autoCapitalize: 'none'
                })}
                {renderFormField('confirm_password', 'Confirm New Password', 'lock-check', {
                  secureTextEntry: !showConfirmPassword,
                  autoCapitalize: 'none'
                })}
              </>
            )}

            {renderSettingItem(
              'Two-Factor Authentication',
              'Add an extra layer of security to your account',
              'two_factor_auth'
            )}
          </View>

          {/* Notification Preferences */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Notification Preferences</Text>
            
            {renderSettingItem(
              'Push Notifications',
              'Receive notifications about jobs, interviews, and updates',
              'notifications'
            )}
            {renderSettingItem(
              'Email Updates',
              'Get important updates via email',
              'email_updates'
            )}
            {renderSettingItem(
              'SMS Notifications',
              'Receive urgent notifications via SMS',
              'sms_notifications'
            )}
            {renderSettingItem(
              'Marketing Emails',
              'Receive promotional emails and career tips',
              'marketing_emails'
            )}
          </View>

          {/* App Preferences */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>App Preferences</Text>
            
            {renderSettingItem(
              'Dark Mode',
              'Use dark theme throughout the app',
              'dark_mode'
            )}
            
            <TouchableOpacity style={styles.settingItem}>
              <View style={styles.settingContent}>
                <Text style={styles.settingTitle}>Language</Text>
                <Text style={styles.settingSubtitle}>Choose your preferred language</Text>
              </View>
              <View style={styles.languageContainer}>
                <Text style={styles.languageText}>{settings.language}</Text>
                <Ionicons name="chevron-forward" size={20} color="#64748b" />
              </View>
            </TouchableOpacity>
          </View>

          {/* Data & Privacy */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Data & Privacy</Text>
            
            <TouchableOpacity style={styles.settingItem}>
              <View style={styles.settingContent}>
                <Text style={styles.settingTitle}>Download My Data</Text>
                <Text style={styles.settingSubtitle}>Get a copy of your data</Text>
              </View>
              <Ionicons name="download" size={20} color="#64748b" />
            </TouchableOpacity>

            <TouchableOpacity style={styles.settingItem}>
              <View style={styles.settingContent}>
                <Text style={styles.settingTitle}>Privacy Policy</Text>
                <Text style={styles.settingSubtitle}>Learn how we protect your data</Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color="#64748b" />
            </TouchableOpacity>

            <TouchableOpacity style={styles.settingItem}>
              <View style={styles.settingContent}>
                <Text style={styles.settingTitle}>Terms of Service</Text>
                <Text style={styles.settingSubtitle}>Read our terms and conditions</Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color="#64748b" />
            </TouchableOpacity>
          </View>

          {/* Danger Zone */}
          <View style={styles.section}>
            <Text style={[styles.sectionTitle, styles.dangerTitle]}>Danger Zone</Text>
            
            <TouchableOpacity 
              style={[styles.settingItem, styles.dangerItem]}
              onPress={handleDeleteAccount}
            >
              <View style={styles.settingContent}>
                <Text style={[styles.settingTitle, styles.dangerText]}>Delete Account</Text>
                <Text style={styles.settingSubtitle}>Permanently delete your account and all data</Text>
              </View>
              <Ionicons name="trash" size={20} color="#ef4444" />
            </TouchableOpacity>
          </View>

          <View style={styles.bottomSpacing} />
        </ScrollView>

        {/* Save Button */}
        <View style={styles.saveContainer}>
          <TouchableOpacity
            style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
            onPress={handleSaveChanges}
            disabled={isSaving}
          >
            {isSaving ? (
              <ActivityIndicator size={20} color="#ffffff" />
            ) : (
              <>
                <MaterialCommunityIcons name="content-save" size={20} color="#ffffff" />
                <Text style={styles.saveButtonText}>Save Changes</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748b',
  },
  header: {
    backgroundColor: '#0e4e96',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    paddingTop: Platform.OS === 'ios' ? 16 : StatusBar.currentHeight + 16,
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#ffffff',
  },
  headerRight: {
    width: 40,
  },
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  section: {
    backgroundColor: '#ffffff',
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 12,
    padding: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 16,
  },
  dangerTitle: {
    color: '#ef4444',
  },
  fieldContainer: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    backgroundColor: '#ffffff',
  },
  inputError: {
    borderColor: '#ef4444',
  },
  inputIcon: {
    marginLeft: 12,
    marginRight: 8,
  },
  input: {
    flex: 1,
    height: 48,
    fontSize: 16,
    color: '#1e293b',
    paddingRight: 12,
  },
  eyeIcon: {
    padding: 12,
  },
  errorText: {
    fontSize: 12,
    color: '#ef4444',
    marginTop: 4,
    marginLeft: 4,
  },
  changePasswordButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#f1f5f9',
  },
  changePasswordText: {
    fontSize: 14,
    color: '#0e4e96',
    fontWeight: '600',
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  settingContent: {
    flex: 1,
  },
  settingTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 2,
  },
  settingSubtitle: {
    fontSize: 14,
    color: '#64748b',
  },
  languageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  languageText: {
    fontSize: 14,
    color: '#64748b',
    marginRight: 8,
  },
  dangerItem: {
    borderBottomColor: '#fecaca',
  },
  dangerText: {
    color: '#ef4444',
  },
  saveContainer: {
    padding: 16,
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
  },
  saveButton: {
    backgroundColor: '#0e4e96',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  saveButtonDisabled: {
    backgroundColor: '#94a3b8',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
    marginLeft: 8,
  },
  bottomSpacing: {
    height: 32,
  },
});
